/**
 * @file SeoPendingNotice.tsx
 * @description Плашка «Неопубликованные правки SEO: … [Применить]
 * [Отбросить]» (phase19.16, партия Р3в, v0.19.64; SB145 «Перенос»;
 * контракт r3v-api-contract §5). При переносе SEO из вида сайта значения
 * взяты из живой версии; если в черновике вида сайта были другие SEO-правки,
 * сервер помнит их в `pending` — молча не теряем: владелец применяет их
 * (действуют сразу) или отбрасывает (с подтверждением).
 *
 * «Применить»: если среди правок «закрыт от поисковиков» — сначала окно со
 * словом «закрыть» (`confirm`, SB145); стоп-лист (409 `site_seo_text_blocked`)
 * — ошибки и галочка владельца (SB125), повтор с `acknowledgeChecks`.
 */

import { useState } from "react";

import { Button } from "../../ui/button";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import { useConfirm } from "../../ui/use-confirm";
import { CloseIndexingDialog } from "./CloseIndexingDialog";
import { StopListAck } from "./StopListAck";
import type { ShopSeoApi } from "../../../hooks/site-seo/useShopSeoSettings";
import { CLOSE_INDEXING_WORD } from "../../../lib/site-seo/close-word";
import { errorCode, seoErrorText, stopListChecks } from "../../../lib/site-seo/seo-errors";
import { pendingFieldsText } from "../../../lib/site-seo/seo-fields";
import type { PublishCheck } from "../../../lib/site-view/types";

interface Props {
  api: ShopSeoApi;
  readOnly: boolean;
}

export function SeoPendingNotice({ api, readOnly }: Props) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<"apply" | "discard" | null>(null);
  const [asking, setAsking] = useState(false);
  const [checks, setChecks] = useState<PublishCheck[]>([]);
  const [ack, setAck] = useState(false);
  const pending = api.settings?.pending ?? null;
  if (!pending || pending.fields.length === 0) return null;
  // Правки закрывают сайт от поисковиков — нужно подтверждение словом.
  const closes = pending.fields.some((f) => f.field === "allowIndexing" && f.value === false);

  async function apply(confirmed = false) {
    if (closes && !confirmed) return setAsking(true);
    setBusy("apply");
    try {
      await api.applyPending({ ...(closes ? { confirm: CLOSE_INDEXING_WORD } : {}), ...(ack && checks.length > 0 ? { acknowledgeChecks: true } : {}) });
      setAsking(false);
      setChecks([]);
      notify.success("Правки применены — действуют на сайте.");
    } catch (err) {
      const found = stopListChecks(err);
      setAsking(false);
      if (found.length > 0) {
        setChecks(found);
        setAck(false);
      } else failed(err);
    } finally {
      setBusy(null);
    }
  }

  async function discard() {
    const ok = await confirm.confirm({
      title: "Отбросить неопубликованные правки SEO?",
      description: "Останутся настройки, которые сейчас действуют на сайте. Правки из черновика вида сайта пропадут.",
      action: "Отбросить",
      danger: true,
    });
    if (!ok) return;
    setBusy("discard");
    try {
      await api.discardPending();
      notify.success("Правки отброшены.");
    } catch (err) {
      failed(err);
    } finally {
      setBusy(null);
    }
  }

  function failed(err: unknown) {
    if (errorCode(err) === "site_seo_nothing_pending") void api.reload();
    if (errorCode(err) !== "site_seo_version_conflict") notify.error(seoErrorText(err, "Не удалось — попробуйте ещё раз."));
  }

  return (
    <>
      <NoticeStrip
        tone="warning"
        title="Неопубликованные правки SEO"
        action={
          readOnly ? undefined : (
            <div className="flex flex-wrap gap-2 max-sm:w-full">
              <Button className="max-sm:flex-1" loading={busy === "apply"} disabled={busy !== null || (checks.length > 0 && !ack)} onClick={() => void apply()}>
                Применить
              </Button>
              <Button variant="outline" className="max-sm:flex-1" loading={busy === "discard"} disabled={busy !== null} onClick={() => void discard()}>
                Отбросить
              </Button>
            </div>
          )
        }
      >
        В черновике вида сайта остались правки, которые не успели опубликовать: {pendingFieldsText(pending)}. SEO теперь действует сразу — примените их или отбросьте.
      </NoticeStrip>
      <StopListAck checks={checks} ack={ack} onAck={setAck} />
      <ConfirmDialog request={confirm.request} onSettle={confirm.settle} />
      <CloseIndexingDialog open={asking} busy={busy === "apply"} onCancel={() => setAsking(false)} onConfirm={() => void apply(true)} />
    </>
  );
}
